/**
 * Filter-related constants and types.
 *
 * These are extracted to a separate file to avoid circular dependencies
 * between useFilterStore and useBuildPreferencesStore.
 */

import { JobStates } from "~/components/build/Show/lib/types";
import { State, Outcome } from "~/lib/pipeline/Step";

export enum FilterByOption {
  Failed = "failed",
  Passed = "passed",
  Running = "running",
  Scheduled = "scheduled",
  SoftFailed = "soft-failed",
  Waiting = "waiting",
  Canceled = "canceled",
  Ignored = "skipped", // For categorization only, not shown in filter dropdown
}

export const StateOrder = [
  FilterByOption.Failed,
  FilterByOption.Canceled,
  FilterByOption.Running,
  FilterByOption.Scheduled,
  FilterByOption.Waiting,
  FilterByOption.SoftFailed,
  FilterByOption.Passed,
  FilterByOption.Ignored,
];

/** Minimal job data needed for filtering. */
export type JobFilterData = {
  state: JobStates;
  passed?: boolean | null;
  softFailed?: boolean | null;
  willFail?: boolean | null;
};

export function isJobFailed(job: JobFilterData): boolean {
  return (
    !job.softFailed &&
    (job.willFail ||
      (job.state === "finished" && !job.passed) ||
      (job.state === "timed_out" && !job.passed) ||
      job.state === "timing_out" ||
      job.state === "expired")
  );
}

export function isJobStatePassed(job: JobFilterData): boolean {
  return (job.state === "finished" || job.state === "timed_out") && Boolean(job.passed);
}

export function isJobRunning(job: JobFilterData): boolean {
  // A running job that will fail is surfaced as failed, not running,
  // so it shows up in only one filter bucket.
  return job.state === "running" && !job.willFail;
}

export function isJobStateScheduled(job: JobFilterData): boolean {
  return (
    job.state === "scheduled" ||
    job.state === "limited" ||
    job.state === "limiting" ||
    job.state === "platform_limited" ||
    job.state === "platform_limiting" ||
    job.state === "reserved" ||
    job.state === "assigned" ||
    job.state === "accepted"
  );
}

export function isJobStateSoftFailed(job: JobFilterData): boolean {
  return Boolean(job.softFailed);
}

export function isJobStateWaiting(job: JobFilterData): boolean {
  return (
    job.state === "pending" ||
    job.state === "waiting" ||
    job.state === "waiting_failed" ||
    job.state === "blocked" ||
    job.state === "blocked_failed"
  );
}

export function isJobStateCanceled(job: JobFilterData): boolean {
  return job.state === "canceled" || job.state === "canceling";
}

/** Jobs don't have an "ignored" state; always returns false. */
export function isJobStateIgnored(_job: JobFilterData): boolean {
  return false;
}

/** Visual metadata for each filter option: label, step state, and outcome for icon rendering.
 *  Note: ordering intentional from most to least important. */
export const FILTER_STATE_CONFIG: Record<
  FilterByOption,
  { label: string; state: State; outcome: Outcome | null }
> = {
  [FilterByOption.Failed]: { label: "Failed", state: State.Finished, outcome: Outcome.HardFailed },
  [FilterByOption.Running]: { label: "Running", state: State.Running, outcome: null },
  [FilterByOption.Scheduled]: { label: "Scheduled", state: State.Ready, outcome: null },
  [FilterByOption.Waiting]: {
    label: "Waiting",
    state: State.WaitingForDependencies,
    outcome: null,
  },
  [FilterByOption.SoftFailed]: {
    label: "Soft failed",
    state: State.Finished,
    outcome: Outcome.SoftFailed,
  },
  [FilterByOption.Passed]: { label: "Passed", state: State.Finished, outcome: Outcome.Passed },
  [FilterByOption.Canceled]: { label: "Canceled", state: State.Canceled, outcome: null },
  [FilterByOption.Ignored]: { label: "Skipped", state: State.Ignored, outcome: null },
};

export const JOB_STATE_FILTERS: Record<FilterByOption, (job: JobFilterData) => boolean> = {
  [FilterByOption.Failed]: isJobFailed,
  [FilterByOption.Passed]: isJobStatePassed,
  [FilterByOption.Running]: isJobRunning,
  [FilterByOption.Scheduled]: isJobStateScheduled,
  [FilterByOption.SoftFailed]: isJobStateSoftFailed,
  [FilterByOption.Waiting]: isJobStateWaiting,
  [FilterByOption.Canceled]: isJobStateCanceled,
  [FilterByOption.Ignored]: isJobStateIgnored,
};
